/**
 * Shared utilities for wallet history time period filtering
 *
 * This module provides utilities for filtering wallet transactions, earnings,
 * expenses, and usage by time period. It includes smart auto-detection to
 * ensure users always see their data when it exists.
 */

export type SummaryPeriod = 'day' | 'week' | 'month' | 'all';

/**
 * Calculate the start timestamp for a given period
 * Returns null for 'all' (no time filtering)
 *
 * @param period - The time period to calculate start for
 * @returns Timestamp in milliseconds, or null for 'all'
 */
export function getPeriodStartTimestamp(period: SummaryPeriod): number | null {
  if (period === 'all') return null;

  const now = Date.now();

  if (period === 'day') {
    // Last 24 hours (rolling window)
    return now - (24 * 60 * 60 * 1000);
  }

  if (period === 'week') {
    // Last 7 days (rolling window)
    return now - (7 * 24 * 60 * 60 * 1000);
  }

  // period === 'month' - Last 30 days (rolling window)
  return now - (30 * 24 * 60 * 60 * 1000);
}

/**
 * Filter items by timestamp for a given period
 *
 * @param items - Array of items with timestamp property
 * @param period - The time period to filter by
 * @returns Filtered array of items
 */
export function filterByPeriod<T extends { timestamp: number }>(
  items: T[],
  period: SummaryPeriod
): T[] {
  const periodStart = getPeriodStartTimestamp(period);

  // If 'all', return all items (no time filtering)
  if (periodStart === null) return items;

  return items.filter(item => item.timestamp >= periodStart);
}

/**
 * Find the smallest period that has data
 * Returns 'day', 'week', 'month', or 'all'
 *
 * This ensures users always see their data by automatically selecting
 * the most relevant time range that contains transactions.
 *
 * @param items - Array of items to check
 * @returns The smallest period containing data
 */
export function findSmallestPeriodWithData<T extends { timestamp: number }>(
  items: T[]
): SummaryPeriod {
  if (items.length === 0) return 'day'; // Default when no data

  const periods: SummaryPeriod[] = ['day', 'week', 'month'];

  for (const period of periods) {
    const filtered = filterByPeriod(items, period);
    if (filtered.length > 0) {
      return period;
    }
  }

  // If no data in day/week/month, must be older
  return 'all';
}

/**
 * Returns which periods have data available
 * Used to disable period buttons when no data exists for that period
 *
 * @param items - Array of items to check
 * @returns Object mapping each period to boolean (true if data exists)
 */
export function getPeriodDataAvailability<T extends { timestamp: number }>(
  items: T[]
): Record<SummaryPeriod, boolean> {
  return {
    day: filterByPeriod(items, 'day').length > 0,
    week: filterByPeriod(items, 'week').length > 0,
    month: filterByPeriod(items, 'month').length > 0,
    all: items.length > 0,
  };
}

/**
 * Period display labels for UI
 */
export const PERIOD_LABELS: Record<SummaryPeriod, string> = {
  day: '24h',
  week: '7d',
  month: '30d',
  all: 'all',
};
